import { useMemo } from 'react';
import orderBy from 'lodash/orderBy';

import {
  INTERCOM_GOOGLE_SIGNIN_HELP_LINK_AU,
  INTERCOM_GOOGLE_SIGNIN_HELP_LINK_US,
  SSO_CENET_PROVIDER_ID,
  SSO_DET_NSW_PROVIDER_ID,
  SSO_GOOGLE_PROVIDER_ID,
} from 'global/constants';
import { HTTP_GET, HTTP_POST } from 'webclient.constants';
import useDownloadAndPrintPdf from 'approot/shared/print-pdf/print-pdf.hooks';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { useCurrentClassVar } from 'approot/classes/classes.hooks';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import { useRegionData } from 'approot/shared/region/region.apollo';
import type { GetClassStudentsQuery } from '../../students/students.graphql.generated';
import {
  SimpleModalContentStyled,
  SimpleModalVerticalActionsStyled,
} from 'lib/modal/simple-modal.styles';
import SimpleModal from 'lib/modal/simple-modal';
import Spinner from 'approot/shared/spinner/spinner';
import { Dropdown } from 'approot/shared/dropdown/dropdown';
import {
  DropdownDivider,
  DropdownItem,
} from 'approot/shared/dropdown/dropdown-item.style';
import {
  AnchorStyled,
  Heading18,
  PStyled,
} from 'approot/shared/styles/typography.style';
import { SecondaryButton } from 'approot/shared/styles/buttons/secondary-button.style';
import IconPrint from 'approot/shared/icons/icon-print';
import * as Styled from './assign-print-options.style';
import { SIGN_IN_CARDS_PDF_FILENAME } from 'approot/shared/assign/assign-print.constants';
import IconDropdown from 'approot/shared/icons/icon-dropdown';
import type { Pluck } from 'lib/gql/pluck';

type Props = {
  students: Pluck<GetClassStudentsQuery, 'getClassStudents'>[];
  isPrimaryVariant?: boolean;
};

const TriggerButtonContent = ({ loading }: { loading?: boolean }) => {
  return (
    <>
      <IconPrint width={24} height={24} />
      Print Sign in Cards
      {loading ? <Spinner /> : <IconDropdown width={10} height={16} />}
    </>
  );
};

const AssignPrintOptions = ({ students, isPrimaryVariant }: Props) => {
  const currentClass = useCurrentClassVar();
  const classId = currentClass?.id;

  const { isRegionAU } = useRegionData();
  const { isVisible, openModal, closeModal } = useSimpleModalState();
  const { showError } = useSnackbar();

  const allStudentsHaveUsernames = useMemo(
    () => students.every(s => s.username),
    [students]
  );

  const { handlePrintClick, loading: loadingPrintClick } =
    useDownloadAndPrintPdf({
      downloadPath: '/api/students/print-student-cards',
      downloadFileName: 'sign-in-cards.pdf',
      downloadMethod: HTTP_POST,
      onError: () => {
        showError(
          'There was a problem generating the printing file. Please try again later.'
        );
      },
    });

  const {
    handlePrintClick: handlePrintClassCodeClick,
    loading: loadingClassCode,
  } = useDownloadAndPrintPdf({
    downloadPath: '/api/classtodo/print-password',
    downloadFileName: SIGN_IN_CARDS_PDF_FILENAME,
    downloadMethod: HTTP_GET,
    onError: () =>
      showError(
        'There was a problem generating the printing file. Please try again later.'
      ),
  });

  const loading = loadingPrintClick || loadingClassCode;

  const sortedStudents = useMemo(
    () => orderBy(students, 'email', 'asc'),
    [students]
  );

  return (
    <>
      {isVisible && (
        <SimpleModal
          isVisible
          title="Check Google Sign in Access"
          headerTitle="Check Google Sign in Access"
          onClose={closeModal}
        >
          <SimpleModalContentStyled>
            <Heading18>
              Make sure your students can sign in with Google.
            </Heading18>
            <PStyled>
              If Google sign-in is blocked, students can still use the QR code
              on their sign-in cards.
            </PStyled>
            <PStyled>
              For more information{' '}
              <AnchorStyled
                target="_blank"
                href={
                  isRegionAU
                    ? INTERCOM_GOOGLE_SIGNIN_HELP_LINK_AU
                    : INTERCOM_GOOGLE_SIGNIN_HELP_LINK_US
                }
                rel="noopener noreferrer"
              >
                click here
              </AnchorStyled>
              .
            </PStyled>
            <SimpleModalVerticalActionsStyled>
              <Styled.AssignPrintPrimaryButton
                type="button"
                onClick={() => {
                  handlePrintClick({
                    userClassId: classId,
                    studentsIds: sortedStudents.map(s => s.id),
                    ssoProvider: SSO_GOOGLE_PROVIDER_ID,
                  });
                  closeModal();
                }}
              >
                <IconPrint width={24} height={24} />
                Print Google Sign in Cards
              </Styled.AssignPrintPrimaryButton>
            </SimpleModalVerticalActionsStyled>
          </SimpleModalContentStyled>
        </SimpleModal>
      )}
      <Dropdown
        menuWidth={allStudentsHaveUsernames ? 336 : 390}
        disabled={loading}
        menuMatchToggleWidth={false}
        dropdownButtonContents={
          isPrimaryVariant ? (
            <Styled.AssignPrintPrimaryButton type="button" disabled={loading}>
              <TriggerButtonContent loading={loading} />
            </Styled.AssignPrintPrimaryButton>
          ) : (
            <SecondaryButton type="button" $withIcon disabled={loading}>
              <TriggerButtonContent loading={loading} />
            </SecondaryButton>
          )
        }
        dropdownContents={
          <>
            <DropdownItem
              onClick={() =>
                handlePrintClick({
                  userClassId: classId,
                  studentsIds: sortedStudents.map(s => s.id),
                  ssoProvider: null,
                })
              }
            >
              {allStudentsHaveUsernames
                ? 'QR Code with Username and Password'
                : 'QR Code with Email or Username and Password'}
            </DropdownItem>
            {!allStudentsHaveUsernames && (
              <>
                <DropdownItem
                  onClick={() => {
                    openModal();
                  }}
                >
                  QR Code with Google
                </DropdownItem>
                {isRegionAU && (
                  <>
                    <DropdownItem
                      onClick={() =>
                        handlePrintClick({
                          userClassId: classId,
                          studentsIds: sortedStudents.map(s => s.id),
                          ssoProvider: SSO_DET_NSW_PROVIDER_ID,
                        })
                      }
                    >
                      QR Code with NSW DoE
                    </DropdownItem>
                    <DropdownItem
                      onClick={() =>
                        handlePrintClick({
                          userClassId: classId,
                          studentsIds: sortedStudents.map(s => s.id),
                          ssoProvider: SSO_CENET_PROVIDER_ID,
                        })
                      }
                    >
                      QR Code with CEnet
                    </DropdownItem>
                  </>
                )}
              </>
            )}
            <DropdownDivider />
            <DropdownItem
              onClick={() =>
                handlePrintClassCodeClick({
                  userClassId: classId,
                })
              }
            >
              QR Code with Class Code
            </DropdownItem>
            <Styled.AssignPrintOptionsText>
              Students signed in with a class code can view content but can’t
              respond.
            </Styled.AssignPrintOptionsText>
          </>
        }
      />
    </>
  );
};

export default AssignPrintOptions;
